import Anthropic, {ClientOptions} from "@anthropic-ai/sdk";
import {MessageParam} from "@anthropic-ai/sdk/resources/messages.js";
import {store} from "../../../Store/index.js";
import {Assert} from "js-vextensions";
import {ShowMessageBox} from "react-vmessagebox";
import moment from "moment";
import {StoryMessageProto} from "../../../Server/DB/storyMessages/@StoryMessage.js";
import {ThinkingType, EffortLevel_ForAnthropic} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_StoryExplore.js";
import {GenerateNextMessageOptions, InsertSummaryDummyMessages, ApplyOmitOldNumberedLists, GenerateNextMessageOptions_Init} from "./@Shared.js";

let anthropic: Anthropic;

export async function GenerateNextMessage_Anthropic(options: GenerateNextMessageOptions_Init) {
	const opts = new GenerateNextMessageOptions(options);
	Assert(opts.apiKey != null, "Anthropic API key is not set.");
	Assert(opts.prevMessages.length > 0, "Cannot generate next message without any previous messages.");
	Assert(!options.blockTrainingAndRetention, `
		Through the Anthropic API directly: Training is off by default, but blocking its 7d/30d retention is not possible.
		(well, through a flag; you would have to get an enterprise account and have it set for ZDR/no-retention at that level)
		If you want true ZDR, the easier way is by using the Bedrock or Vertex providers. (eg. through OpenRouter)
	`.AsMultiline(0));

	if (anthropic == null) {
		const config: ClientOptions = {
			//apiKey: GetDotEnvVar("ANTHROPIC_API_KEY"),
			apiKey: opts.apiKey,
			//baseURL: "https://lucidfrontier.com/anthropic", // commented; anthropic api allows direct calls from browsers now!
			dangerouslyAllowBrowser: true,
		};
		if (opts.baseURLOverride) config.baseURL = opts.baseURLOverride;
		anthropic = new Anthropic(config);
	} else {
		Assert(anthropic.apiKey == opts.apiKey, "Anthropic API key changed; please refresh the page to use the Anthropic API further.");
	}

	let prevMessages_final = InsertSummaryDummyMessages(opts.prevMessages);

	if (opts.omitOldNumberedLists) {
		prevMessages_final = ApplyOmitOldNumberedLists(prevMessages_final, opts);
	}

	const prevMessages_asMessageParams = prevMessages_final.map(a=>({role: a.role, content: a.content} as MessageParam));

	const thinkingEnabled = opts.thinkingType != ThinkingType.disabled;
	const messageStream = await anthropic.messages.create({
		stream: true,
		max_tokens: opts.thinkingType != ThinkingType.disabled ? opts.thinking_budgetTokens + opts.responseTokens : opts.responseTokens,
		thinking:
			opts.thinkingType == ThinkingType.extended ? {type: "enabled" as const, budget_tokens: opts.thinking_budgetTokens} :
			opts.thinkingType == ThinkingType.adaptive ? {type: "adaptive" as const} :
			{type: "disabled" as const},
		system: opts.systemPrompt ?? undefined,
		messages: prevMessages_asMessageParams,
		temperature: thinkingEnabled ? 1 : opts.temperature, // temperature must be 1 when thinking is enabled
		output_config: {effort: EffortLevel_ForAnthropic(opts.effort)},
		// see list here: https://docs.anthropic.com/en/docs/about-claude/models
		model: opts.model,
	}).catch(async err=>{
		console.log("Got headers for anthropic request error:", err.headers);
		if (err instanceof Anthropic.RateLimitError) {
			const resetTime = err.headers ? moment(err.headers["Anthropic-Ratelimit-Tokens-Reset"] + "") : null;
			ShowMessageBox({
				title: "Anthropic API rate limit reached",
				message: `The rate-limit will be reset at: ${resetTime?.format("YYYY-MM-DD HH:mm:ss") ?? "[unknown]"}`,
			});
		} else {
			throw err;
		}
	});
	if (messageStream) {
		for await (const event of messageStream) {
			//console.log("messageStreamEvent:", event);
			switch (event.type) {
				case "message_start":
					break;
				case "message_delta":
					if (event.usage?.output_tokens != null) opts.onOutputTokens?.(event.usage.output_tokens);
					break;
				case "message_stop":
					break;
				case "content_block_start":
					break;
				case "content_block_delta":
					const nextTextSegment = event.delta;
					if (nextTextSegment.type == "text_delta") {
						// sure, there's some overhead to this, but not to a level that actually matters; and this can preserve responses that get off oddly (like a few minutes ago)
						allTextSegmentsStreamedFromAnthropic.push(nextTextSegment.text);

						opts.onTextSegment?.(nextTextSegment.text);
					} else if (nextTextSegment.type == "input_json_delta") {
						opts.onTextSegment?.(JSON.stringify(nextTextSegment.partial_json));
					} else if (nextTextSegment.type == "thinking_delta") {
						opts.onThinkingSegment?.(nextTextSegment.thinking);
					}
					break;
				case "content_block_stop":
					break;
				default:
					// If any event-type defined by library (in typescript) is not listed in the above, the line below will trigger a compile error.
					const exhaustiveCheck: never = event;
					console.error(`An unexpected MessageStreamEvent type was received from Anthropic api. @type:${event["type"]}`);
			}
		}
	}
}
export const allTextSegmentsStreamedFromAnthropic = [] as string[];